import { Port, useAISMapContext } from '../Map/AISMapContext';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { cn, isNonEmpty } from '@ais-ui/utils';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  LoadingSpinner,
  ScrollArea,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
  InfiniteScroll,
} from '@ais-ui/ui-core';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import {
  NextPortArrivalWithDate,
  useNextportPortGet,
  useBulkShipInfo,
} from '@ais-ui/api';
import { RouteButton } from '../utils/RouteButton';
import { PiMagnifyingGlassPlus, PiSortAscending } from 'react-icons/pi';
import { useDebounce } from '@ais-ui/custom-hooks';
import { ErrorDisplay } from '../../components/ErrorDisplay';
import {
  extractTargetInfos,
  getTargetInfoByMMSI,
  InvalidMMSIError,
  ShipInfo,
  TargetTypeEnum,
  useSession,
} from '@ais-ui/websockets';
import { usePermissions } from '../Permissions/PermissionsProvider';

// Sort types for the vessel arrivals
type SortType = 'eta' | 'probability';

const capitalizeName = (name: string | null | undefined) => {
  if (!name) return '';

  return name
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface PortDetailsPanelProps {
  port: Port;
}

export const PortDetailsPanel = React.memo(
  ({ port }: PortDetailsPanelProps) => {
    const [search, setSearch] = useState<string>('');
    const bouncedSearchString = useDebounce(search, 200);
    const [sortType, setSortType] = useState<SortType>('eta');

    return (
      <div className="p-0 pt-0 flex-1 flex flex-col overflow-hidden space-y-4">
        <div className="flex gap-2 px-px pt-1">
          <Input
            id="search"
            placeholder={'Search vessels (min. 3 characters)'}
            className="h-10 flex-1"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <DropdownMenu>
            <Tooltip>
              <DropdownMenuTrigger asChild>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-10 w-10">
                    <PiSortAscending className="size-5" />
                  </Button>
                </TooltipTrigger>
              </DropdownMenuTrigger>
              <TooltipContent>Sort vessels</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSortType('eta')}>
                Sort by arrival time
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortType('probability')}>
                Sort by prediction percentage
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <ArrivingVessels
          port={port}
          bouncedSearchString={bouncedSearchString}
          sortType={sortType}
        />
      </div>
    );
  },
);

interface IdentificationItemProps {
  label: string;
  value: string;
}

const IdentificationItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-muted-foreground flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : 'Unknown'}
      </p>
    </div>
  );
};

interface IdentificationProps {
  port: Port;
}

export const PortIdentification = ({ port }: IdentificationProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-4">
      <IdentificationItem
        label={messages.portDetailsBaseNameLabel}
        value={port.name}
      />
      <IdentificationItem
        label={messages.portDetailsBaseLocodeLabel}
        value={port.locode}
      />
      <IdentificationItem
        label={messages.portDetailsBaseCountryLabel}
        value={port.country}
      />
    </div>
  );
};

interface ArrivingVesselsProps {
  port: Port;
  bouncedSearchString: string;
  sortType: SortType;
}

interface ArrivingVesselsFallBackProps {
  error: any;
}

const ArrivingVesselsFallBack = ({ error }: ArrivingVesselsFallBackProps) => {
  if (error?.response?.parsedBody?.code === 422) {
    return (
      <ErrorDisplay
        srcImage="/422_not_supported_error_bro.png"
        message="Vessel arrival predictions not supported for Port"
        className="h-full pb-40"
      />
    );
  }

  return (
    <ErrorDisplay
      message="Couldn't fetch vessel arrival predictions for the port"
      className="h-full pb-40"
    />
  );
};

const ArrivingVessels = React.memo(
  ({ port, bouncedSearchString, sortType }: ArrivingVesselsProps) => {
    const { messages } = useLocalization();

    const {
      data,
      isLoading: isLoadingNextport,
      isError,
      error,
    } = useNextportPortGet(port.locode);

    const imos = useMemo(
      () => data?.records?.map((arrival) => arrival.imo) || [],
      [data?.records],
    );

    const { data: bulkShipData, isLoading: isLoadingBulkShipInfo } =
      useBulkShipInfo(imos);

    const shipInfoMap = useMemo(() => {
      if (!bulkShipData) return new Map();
      const map = new Map();
      bulkShipData.forEach((shipInfo) => {
        if (shipInfo.imo) {
          map.set(shipInfo.imo, shipInfo);
        }
      });
      return map;
    }, [bulkShipData]);

    // Combine filtering logic into a single useMemo to avoid multiple re-renders
    const filteredArrivals = useMemo(() => {
      if (!data?.records) return [];

      // Only filter if search string has 3 or more characters
      if (!bouncedSearchString || bouncedSearchString.length < 3) {
        return data.records;
      }

      // Filter based on search string - search through ship names and IMOs
      const searchLower = bouncedSearchString.toLowerCase();
      const filtered = data.records.filter((arrival) => {
        const shipInfo = shipInfoMap.get(arrival.imo);
        const shipName = shipInfo?.name || shipInfo?.ship?.name || '';
        const imo = arrival.imo.toString();

        return (
          shipName.toLowerCase().includes(searchLower) ||
          imo.includes(searchLower)
        );
      });
      return filtered;
    }, [data?.records, bouncedSearchString, shipInfoMap]);

    // Make sure we re-sort whenever sortType changes
    const sortedArrivals = useMemo(() => {
      if (!filteredArrivals || filteredArrivals.length === 0) return [];

      const sorted = [...filteredArrivals];

      if (sortType === 'eta') {
        return sorted.sort((a, b) => {
          // Handle cases where eta could be undefined
          if (!a.eta) return 1;
          if (!b.eta) return -1;
          return new Date(a.eta).getTime() - new Date(b.eta).getTime();
        });
      } else if (sortType === 'probability') {
        return sorted.sort((a, b) => {
          // Handle cases where probability is undefined
          const probA = a.probability ?? 0;
          const probB = b.probability ?? 0;
          return probB - probA; // Sort by highest probability first
        });
      }

      return sorted;
    }, [filteredArrivals, sortType]);

    const isLoading = isLoadingNextport || isLoadingBulkShipInfo;

    return (
      <div className="p-0 pt-0 flex-1 flex flex-col overflow-hidden">
        <div className="mb-4">
          <h3 className="text-lg font-medium">
            Vessel arrival predictions ({sortedArrivals.length})
          </h3>
          <Separator className="mt-2" />
        </div>
        {isLoading && <LoadingSpinner />}
        {isError && <ArrivingVesselsFallBack error={error} />}
        {!isLoading && sortedArrivals.length > 0 && (
          <InfiniteScroll
            items={sortedArrivals}
            limit={30}
            observeResize={true}
            className="flex-grow overflow-y-auto border rounded-lg px-2"
            renderItem={(arrival: NextPortArrivalWithDate) => (
              <ArrivingVesselItem
                key={arrival.imo}
                arrival={arrival}
                port={port}
                shipInfo={shipInfoMap.get(arrival.imo)}
              />
            )}
          />
        )}
      </div>
    );
  },
);

interface ArrivingVesselProps {
  arrival: NextPortArrivalWithDate;
  port: Port;
  shipInfo?: any;
}

const ArrivingVesselItem = React.memo(
  ({ arrival, port, shipInfo }: ArrivingVesselProps) => {
    const { formatDateTimeWithTimeZone } = useDateTimeFormat();
    const { toast } = useToast();
    const { olSession } = useSession();

    const { setZoomToShip } = useAISMapContext();
    const { messages } = useLocalization();

    // Extract ship data from the provided shipInfo
    // shipInfo has both flattened fields and a nested ship object
    const ship = shipInfo?.ship || shipInfo;

    // Use ship data if available, otherwise fall back to IMO from arrival
    const displayImo = ship?.imo || arrival.imo;
    const displayMmsi = ship?.mmsi;
    const displayName = ship?.name;

    const onZoomToShip = async () => {
      if (!displayMmsi) {
        toast({
          title: messages.genericToastTitleWarning,
          description: 'MMSI not available for this vessel',
          variant: 'warning',
        });
        return;
      }

      try {
        const result = await getTargetInfoByMMSI(olSession, displayMmsi);

        const extractedTargetInfo = extractTargetInfos(result);

        if (
          extractedTargetInfo.extractedTargetInfoType !== TargetTypeEnum.Ship ||
          extractedTargetInfo.extractedTargetInfo.length === 0
        ) {
          toast({
            title: messages.genericToastTitleError,
            description: 'Could not find ship',
            variant: 'destructive',
          });
          return;
        }

        const aisShip = {
          callSign: (extractedTargetInfo.extractedTargetInfo[0] as ShipInfo)
            .callSign,
          imo: (extractedTargetInfo.extractedTargetInfo[0] as ShipInfo).imo,
          mmsi: (extractedTargetInfo.extractedTargetInfo[0] as ShipInfo).mmsi,
          name: (extractedTargetInfo.extractedTargetInfo[0] as ShipInfo).name,
          type: 'TT_SHIP',
        };

        setZoomToShip(aisShip);
      } catch (error) {
        if (error instanceof InvalidMMSIError) {
          toast({
            title: messages.genericToastTitleWarning,
            description: 'Invalid MMSI number',
            variant: 'warning',
          });
          return;
        }
        throw error;
      }
    };

    const shipName = useMemo(() => {
      return capitalizeName(displayName);
    }, [displayName]);

    return (
      <div className="w-full flex space-x-4 py-2">
        <div className="flex space-x-2 items-center">
          <RouteButton
            mmsi={displayMmsi?.toString()}
            shipName={shipName}
            nextport={arrival}
            portName={port?.name}
          ></RouteButton>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                onClick={onZoomToShip}
                disabled={!displayMmsi}
              >
                <PiMagnifyingGlassPlus className="h-6 w-6"></PiMagnifyingGlassPlus>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Zoom to ship</TooltipContent>
          </Tooltip>
          <Separator orientation="vertical" className="h-14 bg-border" />
        </div>
        <div className="flex-1">
          <div className="flex flex-col space-y-1.5 justify-center">
            <div className="flex space-x-2 items-center">
              <p className="text-sm text-left font-medium leading-none group-hover:underline">
                {shipName ? (
                  shipName
                ) : (
                  <span className="text-muted-foreground">IMO: {displayImo}</span>
                )}
              </p>
            </div>
            <div className="flex space-x-2 items-center">
              <p className="text-sm text-left text-muted-foreground leading-none group-hover:underline">
                MMSI: {displayMmsi || 'Unknown'}
              </p>
              <Separator orientation="vertical" className="h-4 bg-border" />
              <p className="text-sm text-left text-muted-foreground leading-none group-hover:underline">
                IMO: {displayImo || 'Unknown'}
              </p>
            </div>
            <div className="flex space-x-2 items-center">
              <Tooltip>
                <TooltipTrigger>
                  <span
                    className={cn(
                      'block w-2 h-2 rounded-full',
                      (arrival.probability ?? -1) === -1 && 'bg-muted-foreground',
                      (arrival.probability ?? -1) >= 0.5 && 'bg-green-500',
                      (arrival.probability ?? -1) < 0.5 &&
                        (arrival.probability ?? -1) > 0.2 &&
                        'bg-yellow-500',
                      (arrival.probability ?? -1) <= 0.2 && 'bg-red-500',
                    )}
                  ></span>
                </TooltipTrigger>
                <TooltipContent>
                  The probability that {displayName || `IMO ${displayImo}`} will
                  go to {port.name}
                </TooltipContent>
              </Tooltip>
              <p className="text-sm text-left text-muted-foreground">
                {arrival.probability
                  ? `${(arrival.probability * 100).toFixed(1)} %`
                  : 'Unknown'}
              </p>
              <Separator orientation="vertical" className="h-4 bg-border" />
              <p className="text-sm text-left text-muted-foreground">
                {formatDateTimeWithTimeZone(arrival.eta, true)}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  },
);
